/**
 * vessel theme – giftset ui controller with integrated variant picker
 * ENHANCED: Handles preorder date calculation, scroll preservation, and consistent navigation
 * DEBUG VERSION: Enhanced logging to diagnose color/variant issues
 */

class VariantGiftsetPicker extends HTMLElement {
  constructor() {
    super();
    this.state = { current: {}, giftsets: [], types: [], sizes: [] };
    this.controller = new AbortController();
    this.onChange = this.onChange.bind(this);
    this.onClickDelegate = this.onClickDelegate.bind(this);
    this.onVariantChange = this.onVariantChange.bind(this);
    this.isApplyingColor = false;
  }

  connectedCallback() {
    console.log('[giftset-ui] ========== CONNECTED ==========');
    console.log('[giftset-ui] Timestamp:', new Date().toISOString());
    
    this.loadState();
    this.createVariantForm();
    this.wireEvents();
    this.setupThemeVariantPickerListener();
    this.sortSizeOptions();
    
    // Log what UI elements we found
    console.log('[giftset-ui] UI elements found:', {
      colorInputs: this.querySelectorAll('input[name="giftset-color"]').length,
      colorInputValues: Array.from(this.querySelectorAll('input[name="giftset-color"]')).map(i => i.value),
      sizeSelector: !!this.querySelector('[data-giftset-selector="size"]'),
      typeButtons: this.querySelectorAll('[data-giftset-selector="type"]').length
    });
    
    // Log current state
    console.log('[giftset-ui] State after load:', {
      hasState: !!this.state,
      hasCurrent: !!this.state?.current,
      currentSelectedColor: this.state?.current?.selected_color,
      availableColors: this.state?.current?.colors,
      currentVariants: this.state?.current?.variants?.length || 0,
      currentProductId: this.state?.current?.id,
      currentProductTitle: this.state?.current?.title
    });
    
    // Log what variants are available
    if (this.state?.current?.variants) {
      console.log('[giftset-ui] Available variants:', 
        this.state.current.variants.map(v => ({
          id: v.id,
          title: v.title,
          option1: v.option1,
          available: v.available
        }))
      );
    }
    
    setTimeout(() => {
      console.log('[giftset-ui] --- Color initialization timeout executing ---');
      
      const storedColor = this.getStoredColor();
      const stateColor = this.state?.current?.selected_color;
      const firstColor = this.state?.current?.colors?.[0];
      
      const initColor = storedColor || stateColor || firstColor;
      
      console.log('[giftset-ui] Color selection logic:', {
        storedColor,
        stateColor,
        firstColor,
        selectedColor: initColor,
        colorSource: storedColor ? 'sessionStorage' : (stateColor ? 'state' : 'firstAvailable')
      });
      
      // Check if any color radio is already checked
      const checkedRadio = this.querySelector('input[name="giftset-color"]:checked');
      console.log('[giftset-ui] Checked color radio on load:', checkedRadio?.value || 'none');
      
      if (initColor) {
        console.log('[giftset-ui] APPLYING initial color:', initColor);
        this.updateGiftsetColorUI(initColor);
        this.updateVariantSelection(initColor);
        
        // Verify what happened after applying
        setTimeout(() => {
          const newChecked = this.querySelector('input[name="giftset-color"]:checked');
          const variantInput = this.querySelector('#giftset-variant-id');
          const mainFormInput = document.querySelector('form[action="/cart/add"] input[name="id"]');
          
          console.log('[giftset-ui] POST-INIT verification:', {
            checkedRadio: newChecked?.value || 'none',
            giftsetVariantInput: variantInput?.value || 'none',
            mainFormVariantInput: mainFormInput?.value || 'none',
            buttonState: this.getAddToCartButtonState()
          });
        }, 100);
      } else {
        console.warn('[giftset-ui] ⚠️ NO COLOR AVAILABLE TO APPLY!');
      }
    }, 50);
  }

  getAddToCartButtonState() {
    const button = document.querySelector('button[name="add"], .add-to-cart-button');
    if (!button) return 'not found';
    
    return {
      disabled: button.disabled,
      text: button.querySelector('.add-to-cart-text__content')?.textContent || button.textContent,
      isPreorder: button.dataset.isPreorder || button.classList.contains('is-preorder'),
      classes: Array.from(button.classList)
    };
  }

  disconnectedCallback() {
    const colorInputs = this.querySelectorAll('input[name="giftset-color"]');
    colorInputs.forEach(input => {
      input.removeEventListener('change', this.onChange);
    });
    
    const sizeSelect = this.querySelector('[data-giftset-selector="size"]');
    if (sizeSelect) {
      sizeSelect.removeEventListener('change', this.onChange);
    }
    
    this.removeEventListener('change', this.onChange);
    this.removeEventListener('click', this.onClickDelegate);
    
    if (this._buttonObserver) {
      this._buttonObserver.disconnect();
    }
    
    this.controller.abort();
  }

  loadState() {
    const script = this.querySelector('script[type="application/json"]');
    if (!script) {
      console.error('[giftset-ui] ❌ no JSON script found');
      return;
    }
    try {
      this.state = JSON.parse(script.textContent || '{}');
      console.log('[giftset-ui] ✓ state loaded successfully');
    } catch (e) {
      console.error('[giftset-ui] ❌ invalid giftset json', e);
    }
  }

  // Store selected color in sessionStorage for persistence
  getStoredColor() {
    return sessionStorage.getItem('giftset-selected-color') || null;
  }

  setStoredColor(color) {
    sessionStorage.setItem('giftset-selected-color', color);
    console.log('[giftset-ui] 💾 stored color in session:', color);
  }

  /**
   * Sort size options in logical order (S to XXL)
   */
  sortSizeOptions() {
    const sizeSelect = this.querySelector('[data-giftset-selector="size"]');
    if (!sizeSelect) return;

    const sizeOrder = ['xs', 's', 'm', 'l', 'xl', 'xxl', 'xxxl'];
    
    const options = Array.from(sizeSelect.options);
    const currentValue = sizeSelect.value;
    
    options.sort((a, b) => {
      const aKey = a.value.toLowerCase();
      const bKey = b.value.toLowerCase();
      
      const aSizes = aKey.split('-').filter(s => sizeOrder.includes(s));
      const bSizes = bKey.split('-').filter(s => sizeOrder.includes(s));
      
      if (aSizes.length === 0 || bSizes.length === 0) return 0;
      
      const aFirstSize = aSizes[0];
      const bFirstSize = bSizes[0];
      
      const aIndex = sizeOrder.indexOf(aFirstSize);
      const bIndex = sizeOrder.indexOf(bFirstSize);
      
      return aIndex - bIndex;
    });
    
    sizeSelect.innerHTML = '';
    options.forEach(option => {
      sizeSelect.appendChild(option);
    });
    
    sizeSelect.value = currentValue;
    
    console.log('[giftset-ui] sorted size options');
  }

  createVariantForm() {
    console.log('[giftset-ui] creating integrated variant form');
    
    let variantForm = this.querySelector('.giftset-variant-form');
    if (variantForm) {
      variantForm.remove();
    }

    variantForm = document.createElement('form');
    variantForm.className = 'giftset-variant-form';
    variantForm.style.display = 'none';
    variantForm.action = '/cart/add';
    variantForm.method = 'post';
    variantForm.enctype = 'multipart/form-data';

    const variantInput = document.createElement('input');
    variantInput.type = 'hidden';
    variantInput.name = 'id';
    variantInput.id = 'giftset-variant-id';

    const quantityInput = document.createElement('input');
    quantityInput.type = 'hidden';
    quantityInput.name = 'quantity';
    quantityInput.value = '1';

    variantForm.appendChild(variantInput);
    variantForm.appendChild(quantityInput);
    this.appendChild(variantForm);

    console.log('[giftset-ui] ✓ variant form created');
  }

  wireEvents() {
    const colorInputs = this.querySelectorAll('input[name="giftset-color"]');
    colorInputs.forEach(input => {
      input.addEventListener('change', this.onChange);
    });
    
    const sizeSelect = this.querySelector('[data-giftset-selector="size"]');
    if (sizeSelect) {
      sizeSelect.addEventListener('change', this.onChange);
    }
    
    this.addEventListener('change', this.onChange);
    this.addEventListener('click', this.onClickDelegate);
    console.log('[giftset-ui] ✓ events wired');
  }

  setupThemeVariantPickerListener() {
    const observeButton = () => {
      const addToCartButton = document.querySelector('button[name="add"], .add-to-cart-button');
      if (!addToCartButton) {
        console.warn('[giftset-ui] ⚠️ add-to-cart button not found for observation');
        return;
      }
      
      const observer = new MutationObserver((mutations) => {
        const textContent = addToCartButton.querySelector('.add-to-cart-text__content');
        const currentColor = this.state?.current?.selected_color;
        
        if (textContent && currentColor && !this._isSyncingWithTheme && !this.isApplyingColor) {
          console.log('[giftset-ui] 🔄 detected theme variant picker update, re-applying preorder date');
          
          const highestPreorderDate = this.calculateHighestPreorderDate(currentColor);
          if (highestPreorderDate) {
            setTimeout(() => {
              this.updatePreorderInformation(highestPreorderDate, true);
            }, 50);
          }
        }
      });
      
      observer.observe(addToCartButton, {
        childList: true,
        subtree: true,
        characterData: true
      });
      
      this._buttonObserver = observer;
      console.log('[giftset-ui] ✓ setup button mutation observer');
    };
    
    setTimeout(observeButton, 100);
  }

  onClickDelegate(e) {
    const btn = e.target.closest?.('[data-giftset-selector="type"]');
    if (btn) {
      const type = btn.value;
      // Deliberately not passing productUrl: the Liquid loop bakes in whichever
      // size variant Shopify returned first for this type (often M/L/XL), so
      // matching by URL would ignore the current page's size context. Passing
      // only `type` lets navigate() preserve state.current.size_key and fall
      // back to the first product of that type only if the current size has
      // no match.
      console.log('[giftset-ui] 🖱️ type click ->', { type });
      this.navigate({ type });
    }
  }

  onChange(e) {
    console.log('[giftset-ui] onChange triggered:', {
      target: e.target.tagName,
      name: e.target.name,
      value: e.target.value,
      type: e.target.type
    });
    
    const el = e.target;
    if (!el) return;

    if (el.getAttribute('name') === 'giftset-color') {
      if (this.isApplyingColor) {
        console.log('[giftset-ui] ⏸️ skipping color change (already applying)');
        return;
      }
      
      const color = el.value;
      console.log('[giftset-ui] 🎨 color change ->', color);
      this.onColor(color);
      return;
    }

    if (el instanceof HTMLSelectElement && el.dataset.giftsetSelector === 'size') {
      e.preventDefault();
      e.stopPropagation();
      console.log('[giftset-ui] 📏 size change ->', el.value);
      this.navigate({ size_key: el.value });
      return false;
    }
  }

  onVariantChange(e) {
    console.log('[giftset-ui] variant form changed');
  }

  updateGiftsetColorUI(color) {
    console.log('[giftset-ui] 🎨 updateGiftsetColorUI:', color);
    const radios = this.querySelectorAll('input[type="radio"][name="giftset-color"]');
    console.log('[giftset-ui] found', radios.length, 'color radios');
    
    radios.forEach(r => {
      const wasChecked = r.checked;
      r.checked = (r.value === color);
      
      if (wasChecked !== r.checked) {
        console.log('[giftset-ui] changed radio:', {
          value: r.value,
          from: wasChecked,
          to: r.checked
        });
      }
    });

    this.updateColorAvailabilityClasses();
  }

  updateColorAvailabilityClasses() {
    console.log('[giftset-ui] 🔄 updating color availability classes');
    const colorLabels = this.querySelectorAll('.giftset-ui__color');
    if (!this.state?.current?.variants || !Array.isArray(this.state.current.variants)) {
      console.warn('[giftset-ui] state variants not available to update classes');
      return;
    }

    const lowStockThreshold = this.state.low_stock_threshold || 6;

    colorLabels.forEach(label => {
      const color = label.dataset.color;
      const variant = this.state.current.variants.find(v => v.option1 === color);
      
      let statusClass = 'available';
      if (!variant || variant.available === false) {
        statusClass = 'sold-out';
      } else if (variant.inventory_quantity != null && variant.inventory_quantity <= 0 && variant.inventory_policy === 'continue') {
        statusClass = 'pre-order';
      } else if (variant.inventory_management != null && variant.inventory_management !== '' && variant.inventory_quantity > 0 && variant.inventory_quantity <= lowStockThreshold) {
        statusClass = 'low-stock';
      }

      label.classList.remove('available', 'pre-order', 'low-stock', 'sold-out');
      label.classList.add(statusClass);
      
      console.log(`[giftset-ui] color "${color}" assigned status class: "${statusClass}" (threshold: ${lowStockThreshold})`);
    });
  }

  onColor(color) {
    console.log('[giftset-ui] 🎨 onColor called:', color);
    
    if (this.isApplyingColor) {
      console.log('[giftset-ui] ⏸️ color change blocked (already applying)');
      return;
    }
    
    this.isApplyingColor = true;
    this.state.current.selected_color = color;
    this.setStoredColor(color);
    
    try {
      this.updateVariantSelection(color);
    } finally {
      setTimeout(() => {
        this.isApplyingColor = false;
        console.log('[giftset-ui] ✓ color apply flag reset');
      }, 100);
    }
  }

  getVariantIdForColor(color) {
    console.log('[giftset-ui] 🔍 getVariantIdForColor:', color);
    
    // First check state variants
    if (this.state?.current?.variants && Array.isArray(this.state.current.variants)) {
      console.log('[giftset-ui] searching', this.state.current.variants.length, 'state variants');
      
      const matchingVariant = this.state.current.variants.find(variant => {
        const allMatch = variant.option1 === color && 
                        variant.option2 === color && 
                        variant.option3 === color;
        
        if (allMatch) {
          console.log('[giftset-ui] ✓ MATCH in state:', {
            id: variant.id,
            title: variant.title,
            options: [variant.option1, variant.option2, variant.option3]
          });
        }
        
        return allMatch;
      });
      
      if (matchingVariant) {
        return matchingVariant.id.toString();
      }
    }
    
    // Try other data sources
    let productData = null;
    
    const dataSources = [
      () => {
        const themeVariantPicker = document.querySelector('variant-picker');
        if (themeVariantPicker) {
          const scriptTag = themeVariantPicker.querySelector('script[type="application/json"]');
          if (scriptTag) {
            try {
              const variantData = JSON.parse(scriptTag.textContent || '{}');
              console.log('[giftset-ui] found theme variant data');
              return { source: 'theme', data: variantData };
            } catch (e) {
              console.warn('[giftset-ui] ❌ failed to parse theme variant data:', e);
            }
          }
        }
        return null;
      },
      
      () => {
        if (window.ShopifyAnalytics?.meta?.product) {
          console.log('[giftset-ui] found ShopifyAnalytics product data');
          return { source: 'analytics', data: window.ShopifyAnalytics.meta.product };
        }
        return null;
      },
      
      () => {
        if (window.product && window.product.variants) {
          console.log('[giftset-ui] found global product data');
          return { source: 'global', data: window.product };
        }
        return null;
      },
      
      () => {
        const productIdMeta = document.querySelector('meta[name="product-id"]');
        if (productIdMeta) {
          console.log('[giftset-ui] found product meta tag');
          return { source: 'meta', data: { id: productIdMeta.content } };
        }
        return null;
      }
    ];
    
    for (const getSource of dataSources) {
      const result = getSource();
      if (result) {
        productData = result.data;
        console.log(`[giftset-ui] using ${result.source} data source`);
        break;
      }
    }
    
    if (!productData) {
      console.warn('[giftset-ui] ⚠️ no product data available, using fallback mapping');
      return this.getFallbackVariantId(color);
    }
    
    const matchingVariant = this.findMatchingVariant(productData, color);
    
    if (matchingVariant) {
      const options = matchingVariant.variant_options || [matchingVariant.option1, matchingVariant.option2, matchingVariant.option3];
      console.log('[giftset-ui] ✓ found matching variant:', {
        id: matchingVariant.id,
        title: matchingVariant.title || matchingVariant.variant_title,
        options: options
      });
      return matchingVariant.id.toString();
    }
    
    console.warn('[giftset-ui] ⚠️ no matching variant found for color:', color);
    return this.getFallbackVariantId(color);
  }

  findMatchingVariant(productData, color) {
    let variants = [];
    
    if (productData.variants && Array.isArray(productData.variants)) {
      variants = productData.variants;
    } else if (productData.product?.variants && Array.isArray(productData.product.variants)) {
      variants = productData.product.variants;
    } else if (productData.id && (productData.option1 || productData.variant_options)) {
      variants = [productData];
    }
    
    console.log('[giftset-ui] searching through', variants.length, 'variants');
    
    const matchingVariant = variants.find(variant => {
      let option1, option2, option3;
      
      if (variant.variant_options && Array.isArray(variant.variant_options)) {
        [option1, option2, option3] = variant.variant_options;
      } else if (variant.option1 !== undefined) {
        option1 = variant.option1;
        option2 = variant.option2;
        option3 = variant.option3;
      } else if (variant.options && Array.isArray(variant.options)) {
        [option1, option2, option3] = variant.options;
      }
      
      const allMatch = option1 === color && option2 === color && option3 === color;
      
      return allMatch;
    });
    
    return matchingVariant || null;
  }

  getFallbackVariantId(color) {
    const productId = this.state.current.id;
    
    const variantMap = {
      '15800657510745': {
        'Wit': '56146261213529',
        'Bruin': '56146261442905'
      }
    };
    
    const variantId = variantMap[productId]?.[color];
    
    if (variantId) {
      console.log('[giftset-ui] ✓ using fallback variant mapping:', { color, variantId });
    } else {
      console.warn('[giftset-ui] ⚠️ no fallback variant mapping for:', { productId, color });
    }
    
    return variantId || null;
  }

  calculateHighestPreorderDate(color) {
    console.log('[giftset-ui] 📅 calculating highest preorder date for:', color);
    
    let highestDate = null;
    let highestDateTimestamp = 0;
    
    const bundleItems = document.querySelectorAll('.bundle-contents__item');
    console.log('[giftset-ui] checking', bundleItems.length, 'bundle items');
    
    bundleItems.forEach((item, index) => {
      const variantText = item.querySelector('.bundle-contents__variant')?.textContent || '';
      const itemColor = variantText.split('/')[0]?.trim();
      
      if (itemColor === color) {
        const stockElement = item.querySelector('.bundle-contents__stock--preorder');
        
        if (stockElement) {
          const stockText = stockElement.textContent || '';
          const dateMatch = stockText.match(/Pre-order\s*-\s*(.+)/i);
          
          if (dateMatch) {
            const dateStr = dateMatch[1].trim();
            const timestamp = this.parseDutchDate(dateStr);
            
            console.log(`[giftset-ui] item ${index}:`, {
              variantText,
              dateStr,
              timestamp: new Date(timestamp).toISOString(),
              isHighest: timestamp > highestDateTimestamp
            });
            
            if (timestamp > highestDateTimestamp) {
              highestDateTimestamp = timestamp;
              highestDate = dateStr;
              console.log('[giftset-ui] ✓ NEW highest date:', dateStr);
            }
          }
        }
      }
    });
    
    console.log('[giftset-ui] 📅 FINAL highest preorder date:', highestDate);
    
    return highestDate;
  }

  parseDutchDate(dateStr) {
    // Accepts Dutch and English month names (full + 3-letter abbreviations).
    // The bundle_contents Liquid uses `| date: "%d %B %Y" | downcase`, which renders
    // in the storefront's active locale — that can come back as "29 mei 2026"
    // (Dutch) or "29 may 2026" (English) depending on locale fallbacks. Without
    // English support here, the giftset preorder input never got created on
    // English-rendered date strings.
    const monthMap = {
      // Dutch — full
      'januari': 0, 'februari': 1, 'maart': 2, 'april': 3,
      'mei': 4, 'juni': 5, 'juli': 6, 'augustus': 7,
      'september': 8, 'oktober': 9, 'november': 10, 'december': 11,
      // Dutch — 3-letter
      'jan': 0, 'feb': 1, 'mrt': 2, 'apr': 3, 'jun': 5, 'jul': 6,
      'aug': 7, 'sep': 8, 'okt': 9, 'nov': 10, 'dec': 11,
      // English — full
      'january': 0, 'february': 1, 'march': 2,
      'may': 4, 'june': 5, 'july': 6, 'august': 7,
      // English — 3-letter (mar/oct unique to English; jan/feb/apr/etc shared with Dutch above)
      'mar': 2, 'oct': 9, 'sept': 8
    };

    const parts = dateStr.toLowerCase().trim().split(/\s+/);

    if (parts.length !== 3) {
      console.warn('[giftset-ui] ⚠️ invalid date format:', dateStr);
      return 0;
    }

    const day = parseInt(parts[0]);
    const monthName = parts[1];
    const month = monthMap[monthName];
    const year = parseInt(parts[2]);

    if (isNaN(day) || month === undefined || isNaN(year)) {
      console.warn('[giftset-ui] ⚠️ date parsing failed:', { day, monthName, month, year });
      return 0;
    }

    return new Date(year, month, day).getTime();
  }

  updateVariantSelection(color) {
    console.log('[giftset-ui] ========== UPDATE VARIANT SELECTION ==========');
    console.log('[giftset-ui] Color:', color);
    
    const targetVariantId = this.getVariantIdForColor(color);
    const isAvailable = this.checkColorAvailability(color);
    
    console.log('[giftset-ui] Target variant:', {
      id: targetVariantId,
      isAvailable
    });
    
    // Update internal form
    const variantInput = this.querySelector('#giftset-variant-id');
    if (variantInput && targetVariantId) {
      variantInput.value = targetVariantId;
      console.log('[giftset-ui] ✓ set internal variant ID:', targetVariantId);
    } else {
      console.warn('[giftset-ui] ⚠️ could not set internal variant ID:', {
        hasInput: !!variantInput,
        hasId: !!targetVariantId
      });
    }
    
    // Update main product form
    this.updateMainFormVariantId(targetVariantId);
    
    // Sync with theme picker
    this.syncWithThemeVariantPicker(color, targetVariantId);
    
    // Filter bundle content
    this.filterBundleContent(color);
    
    // Calculate preorder date
    const highestPreorderDate = this.calculateHighestPreorderDate(color);
    console.log('[giftset-ui] Highest preorder date:', highestPreorderDate);
    
    // Update preorder info and button
    this.updatePreorderInformation(highestPreorderDate, isAvailable);
    this.updateAddToCartState(isAvailable, color, highestPreorderDate);
    
    // Dispatch event
    if (targetVariantId) {
      const variantChangeEvent = new CustomEvent('variantchange', {
        detail: {
          variant: {
            id: targetVariantId,
            available: isAvailable,
            option1: color,
            option2: color,
            option3: color,
            preorderDate: highestPreorderDate
          }
        },
        bubbles: true
      });
      this.dispatchEvent(variantChangeEvent);
      console.log('[giftset-ui] ✓ dispatched variantchange event');
    }
    
    console.log('[giftset-ui] ========== VARIANT SELECTION COMPLETE ==========');
  }

  updateMainFormVariantId(variantId) {
    if (!variantId) {
      console.warn('[giftset-ui] ⚠️ no variant ID to update main form');
      return;
    }
    
    console.log('[giftset-ui] 📝 updating main form variant ID:', variantId);
    
    const productForms = [
      document.querySelector('form[action="/cart/add"]'),
      document.querySelector('.product-form'),
      document.querySelector('[data-product-form]'),
      document.querySelector('.shopify-product-form'),
      document.querySelector('product-form form')
    ].filter(Boolean);
    
    console.log('[giftset-ui] found', productForms.length, 'product forms');
    
    productForms.forEach((form, index) => {
      let variantIdInput = form.querySelector('input[name="id"]');
      if (!variantIdInput) {
        variantIdInput = form.querySelector('select[name="id"]');
      }
      
      if (variantIdInput) {
        const oldValue = variantIdInput.value;
        variantIdInput.value = variantId;
        console.log(`[giftset-ui] ✓ updated form ${index}:`, {
          oldValue,
          newValue: variantId
        });
        
        if (variantIdInput.tagName === 'SELECT') {
          const option = Array.from(variantIdInput.options).find(opt => opt.value === variantId);
          if (option) {
            option.selected = true;
          }
        }
      } else {
        console.warn(`[giftset-ui] ⚠️ no variant ID input found in form ${index}`);
      }
    });
    
    const productContainers = document.querySelectorAll('[data-product-id], [data-variant-id]');
    productContainers.forEach(container => {
      if (container.dataset.variantId !== undefined) {
        container.dataset.variantId = variantId;
        console.log('[giftset-ui] ✓ updated container data-variant-id');
      }
    });
  }

  updatePreorderInformation(preorderDate, isAvailable) {
    console.log('[giftset-ui] 📅 updating preorder information:', { preorderDate, isAvailable });

    // Use querySelectorAll so any stale duplicate inputs (e.g. left over from a
    // previous server render or a re-render race) are also cleaned up. Without this,
    // the in-stock "Bruin" colour was inheriting a "Wit" pre-order date because
    // a stale preorder input survived the colour change.
    const preorderInputs = document.querySelectorAll('input.preorder-property[name="properties[Pre-order]"]');

    if (preorderDate) {
      if (preorderInputs.length === 0) {
        const addToCartComponent = document.querySelector('add-to-cart-component');
        if (addToCartComponent) {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = 'properties[Pre-order]';
          input.className = 'preorder-property';
          input.value = preorderDate;
          addToCartComponent.appendChild(input);
          console.log('[giftset-ui] ✓ created preorder property input');
        }
      } else {
        // Update the first input, remove any extras to keep one source of truth.
        preorderInputs.forEach((input, idx) => {
          if (idx === 0) {
            input.value = preorderDate;
          } else {
            input.remove();
          }
        });
        console.log('[giftset-ui] ✓ updated preorder property:', preorderDate);
      }

      this.updateAddToCartButtonText(preorderDate);
    } else {
      preorderInputs.forEach((input) => input.remove());
      if (preorderInputs.length > 0) {
        console.log('[giftset-ui] ✓ removed', preorderInputs.length, 'preorder property input(s)');
      }
      this.updateAddToCartButtonText(null);
    }
  }

  updateAddToCartButtonText(preorderDate) {
    console.log('[giftset-ui] 🛒 updating add-to-cart button text:', preorderDate);
    
    const addToCartButtons = document.querySelectorAll('button[name="add"], .add-to-cart-button');
    const addToCartComponent = document.querySelector('add-to-cart-component');
    
    const preorderText = addToCartComponent?.dataset.preorderText || 'Pre-order';
    const addToCartText = addToCartComponent?.dataset.addToCartText || 'In winkelmand';
    
    console.log('[giftset-ui] button config:', { preorderText, addToCartText });
    console.log('[giftset-ui] found', addToCartButtons.length, 'buttons');
    
    addToCartButtons.forEach((btn, index) => {
      const textContent = btn.querySelector('.add-to-cart-text__content');
      if (!textContent) {
        console.warn(`[giftset-ui] ⚠️ button ${index} has no text content element`);
        return;
      }
      
      // Visible label is always the default "Add to cart" text. Pre-order state
      // is still flagged via the is-preorder class / data-is-preorder so styling
      // and the hidden properties[Pre-order] input continue to work — but the
      // user-facing button label stays consistent regardless of stock state.
      textContent.textContent = addToCartText;
      if (preorderDate) {
        btn.classList.add('is-preorder');
        btn.dataset.isPreorder = 'true';
        console.log(`[giftset-ui] ✓ button ${index} set to preorder (label kept as default):`, addToCartText);
      } else {
        btn.classList.remove('is-preorder');
        btn.dataset.isPreorder = 'false';
        console.log(`[giftset-ui] ✓ button ${index} set to normal:`, addToCartText);
      }
    });
  }

  syncWithThemeVariantPicker(color, targetVariantId) {
    console.log('[giftset-ui] 🔄 syncing with theme variant picker:', { color, targetVariantId });
    
    const themeVariantPicker = this.querySelector('variant-picker') || document.querySelector('variant-picker');
    if (!themeVariantPicker) {
      console.log('[giftset-ui] no theme variant picker found');
      return;
    }
    
    console.log('[giftset-ui] ✓ found theme variant picker');
    
    // Set flag to prevent infinite loops
    this._isSyncingWithTheme = true;
    
    // Find all radio inputs that match our color
    const allRadioInputs = themeVariantPicker.querySelectorAll('input[type="radio"]');
    const radioInputsToClick = [];
    
    console.log('[giftset-ui] found', allRadioInputs.length, 'radio inputs total');
    
    // Group radios by fieldset/option
    const radioGroups = new Map();
    allRadioInputs.forEach(radio => {
      const radioName = radio.name;
      if (!radioGroups.has(radioName)) {
        radioGroups.set(radioName, []);
      }
      radioGroups.get(radioName).push(radio);
    });
    
    console.log('[giftset-ui] found', radioGroups.size, 'radio groups');
    
    // For each group, find the radio matching our color
    radioGroups.forEach((radios, groupName) => {
      const matchingRadio = radios.find(r => r.value === color);
      
      if (matchingRadio) {
        console.log(`[giftset-ui] 🎯 found matching radio in group "${groupName}":`, {
          value: matchingRadio.value,
          variantId: matchingRadio.dataset.variantId,
          currentlyChecked: matchingRadio.checked
        });
        
        // Only click if not already checked
        if (!matchingRadio.checked) {
          radioInputsToClick.push(matchingRadio);
        } else {
          console.log('[giftset-ui] ⏭️ radio already checked, skipping click');
        }
      } else {
        console.warn(`[giftset-ui] ⚠️ no radio found for color "${color}" in group "${groupName}"`);
      }
    });
    
    console.log('[giftset-ui] will click', radioInputsToClick.length, 'radio buttons');
    
    // Click each radio button to trigger theme's event listeners
    radioInputsToClick.forEach((radio, index) => {
      console.log(`[giftset-ui] 🖱️ clicking radio ${index + 1}/${radioInputsToClick.length}:`, {
        name: radio.name,
        value: radio.value,
        variantId: radio.dataset.variantId
      });
      
      // First set checked (in case click handler checks this)
      radio.checked = true;
      
      // Trigger click event
      radio.click();
      
      // Also dispatch change event as backup
      const changeEvent = new Event('change', { bubbles: true });
      radio.dispatchEvent(changeEvent);
    });
    
    // Update the JSON data in the variant picker
    const scriptTag = themeVariantPicker.querySelector('script[type="application/json"]');
    if (scriptTag && targetVariantId) {
      try {
        const currentData = JSON.parse(scriptTag.textContent || '{}');
        const newVariantId = parseInt(targetVariantId);
        
        if (currentData.id !== newVariantId) {
          console.log('[giftset-ui] 📝 updating theme variant picker JSON data');
          
          currentData.id = newVariantId;
          currentData.option1 = color;
          currentData.option2 = color;
          currentData.option3 = color;
          currentData.title = `${color} / ${color}`;
          currentData.variant_title = `${color} / ${color}`;
          
          if (currentData.variant_options) {
            currentData.variant_options = [color, color];
          }
          
          if (currentData.options) {
            currentData.options = [color, color];
          }
          
          scriptTag.textContent = JSON.stringify(currentData);
          console.log('[giftset-ui] ✓ updated theme variant picker JSON:', currentData);
        }
      } catch (e) {
        console.warn('[giftset-ui] ❌ failed to update theme variant data:', e);
      }
    }
    
    // Verify the update worked
    setTimeout(() => {
      const updatedRadios = themeVariantPicker.querySelectorAll('input[type="radio"]:checked');
      console.log('[giftset-ui] 🔍 verification - checked radios after click:', 
        Array.from(updatedRadios).map(r => ({
          name: r.name,
          value: r.value,
          variantId: r.dataset.variantId
        }))
      );
      
      const buttonState = this.getAddToCartButtonState();
      console.log('[giftset-ui] 🔍 verification - button state after click:', buttonState);
      
      this._isSyncingWithTheme = false;
    }, 150);
    
    // Dispatch our custom event
    const variantChangeEvent = new CustomEvent('giftset:variant:change', {
      detail: {
        variant: {
          id: targetVariantId,
          available: true,
          option1: color,
          option2: color,
          option3: color
        }
      },
      bubbles: true
    });
    this.dispatchEvent(variantChangeEvent);
    console.log('[giftset-ui] ✓ dispatched giftset:variant:change event');
  }

  checkColorAvailability(color) {
    console.log('[giftset-ui] ✓ checking availability for:', color);
    
    const bundleItems = document.querySelectorAll('.bundle-contents__item');
    let unavailableCount = 0;
    
    bundleItems.forEach((item, index) => {
      const isUnavailable = item.classList.contains('bundle-contents__item--unavailable');
      const variantText = item.querySelector('.bundle-contents__variant')?.textContent || '';
      const itemColor = variantText.split('/')[0]?.trim();
      
      if (itemColor === color && isUnavailable) {
        unavailableCount++;
        console.log(`[giftset-ui] ⚠️ item ${index} (${color}) is unavailable`);
      }
    });
    
    const isFullyAvailable = unavailableCount === 0;
    console.log('[giftset-ui] availability result:', {
      color,
      unavailableCount,
      isFullyAvailable
    });
    
    return isFullyAvailable;
  }

  filterBundleContent(selectedColor) {
    console.log('[giftset-ui] 🔍 filtering bundle content for:', selectedColor);
    
    const bundleItems = document.querySelectorAll('.bundle-contents__item');
    let visibleCount = 0;
    let hiddenCount = 0;
    
    bundleItems.forEach((item, index) => {
      const variantElement = item.querySelector('.bundle-contents__variant');
      const variantText = variantElement?.textContent || '';
      const itemColor = variantText.split('/')[0]?.trim();
      
      if (itemColor === selectedColor) {
        item.style.display = '';
        item.classList.remove('bundle-item--hidden');
        item.classList.add('bundle-item--visible');
        visibleCount++;
      } else if (itemColor && itemColor !== selectedColor) {
        item.style.display = 'none';
        item.classList.add('bundle-item--hidden');
        item.classList.remove('bundle-item--visible');
        hiddenCount++;
      } else {
        item.style.display = '';
        item.classList.remove('bundle-item--hidden');
      }
    });
    
    console.log('[giftset-ui] ✓ filter complete:', {
      selectedColor,
      visibleCount,
      hiddenCount,
      totalItems: bundleItems.length
    });
    
    this.updateBundleHeader(selectedColor, visibleCount);
  }

  updateAddToCartState(isAvailable, color, preorderDate) {
    console.log('[giftset-ui] 🛒 updating add-to-cart state:', { isAvailable, color, preorderDate });
    
    const addToCartButtons = document.querySelectorAll(
      'button[name="add"], input[name="add"], .btn--add-to-cart, [data-add-to-cart], button[type="submit"], .add-to-cart-button'
    );
    
    console.log('[giftset-ui] found', addToCartButtons.length, 'add-to-cart buttons');
    
    let availabilityMessage = document.getElementById('giftset-availability-message');
    if (!availabilityMessage) {
      availabilityMessage = document.createElement('div');
      availabilityMessage.id = 'giftset-availability-message';
      availabilityMessage.style.cssText = `
        margin-top: 8px; 
        padding: 8px 12px; 
        border-radius: 4px; 
        font-size: 0.9rem;
        font-weight: 500;
      `;
      
      const insertAfter = this.querySelector('.giftset-ui__sizes') || this;
      if (insertAfter.parentNode) {
        insertAfter.parentNode.insertBefore(availabilityMessage, insertAfter.nextSibling);
      } else {
        this.appendChild(availabilityMessage);
      }
    }
    
    addToCartButtons.forEach((btn, index) => {
      if (isAvailable) {
        btn.disabled = false;
        btn.classList.remove('btn--sold-out', 'sold-out');
        btn.classList.add('available');
        console.log(`[giftset-ui] ✓ enabled button ${index}`);
      } else {
        btn.disabled = true;
        btn.classList.add('btn--sold-out', 'sold-out');
        btn.classList.remove('available');
        console.log(`[giftset-ui] ⚠️ disabled button ${index}`);
      }
    });
    
    if (isAvailable) {
      if (preorderDate) {
        availabilityMessage.style.cssText += 'background-color: #fff3cd; color: #856404;';
        availabilityMessage.textContent = `Pre-order - ${preorderDate}`;
      } else {
        availabilityMessage.style.cssText += 'background-color: #d4edda; color: #155724;';
        availabilityMessage.textContent = `Alle items beschikbaar in ${color}`;
      }
    } else {
      availabilityMessage.style.cssText += 'background-color: #f8d7da; color: #721c24;';
      availabilityMessage.textContent = `Sommige items zijn niet beschikbaar in ${color.toLowerCase()}`;    
    }
    
    availabilityMessage.style.display = 'block';
  }

  updateBundleHeader(selectedColor, visibleCount) {
    const bundleHeader = document.querySelector('.bundle-contents__header, .bundle-header, .bundle-contents h3, .bundle-contents h4');
    if (!bundleHeader) return;
    
    let filterStatus = bundleHeader.querySelector('.bundle-filter-status');
    if (!filterStatus) {
      filterStatus = document.createElement('span');
      filterStatus.className = 'bundle-filter-status';
      filterStatus.style.cssText = `
        font-size: 0.875rem;
        color: #666;
        font-weight: normal;
        margin-left: 8px;
      `;
      bundleHeader.appendChild(filterStatus);
    }
    
    if (visibleCount > 0) {
      filterStatus.textContent = `(${visibleCount} items in ${selectedColor})`;
      filterStatus.style.display = 'inline';
    } else {
      filterStatus.style.display = 'none';
    }
  }

  navigate(sel) {
    const type = sel.type || this.state.current.type;
    const size_key = sel.size_key || this.state.current.size_key;
    const currentColor = this.state.current.selected_color;
    const { giftsets } = this.state;

    const debugLog = sessionStorage.getItem('giftset-debug-log') || '';
    const timestamp = new Date().toISOString();
    sessionStorage.setItem('giftset-debug-log', debugLog + `\n[${timestamp}] navigate() called with: ${JSON.stringify(sel)}`);

    console.log('[giftset-ui] 🧭 navigate() input:', {
      requested: sel,
      currentColor: currentColor,
      resolvedType: type,
      resolvedSizeKey: size_key
    });

    let match = null;

    if (sel.productUrl) {
      match = giftsets.find(g => g.url === sel.productUrl);
    } else if (sel.type && !sel.size_key) {
      console.log('[giftset-ui] type change detected, preserving current size:', this.state.current.size_key);
      const typeMatches = giftsets.filter(g => (g.type || '') === sel.type);
      
      match = typeMatches.find(g => (g.size_key || '') === this.state.current.size_key);
      
      if (!match) {
        match = typeMatches[0];
        console.log('[giftset-ui] same size not available for new type, using fallback');
      } else {
        console.log('[giftset-ui] found matching type and size');
      }
    } else if (type && size_key) {
      match = giftsets.find(g => (g.type || '') === type && (g.size_key || '') === size_key);
    } else if (type) {
      const typeMatches = giftsets.filter(g => (g.type || '') === type);
      match = typeMatches.find(g => (g.size_key || '') === size_key) || typeMatches[0];
    }

    if (!match) {
      console.warn('[giftset-ui] ⚠️ no product match found for:', { type, size_key, requestedType: sel.type });
      return;
    }

    console.log('[giftset-ui] ✓ SELECTED PRODUCT:', {
      id: match.id,
      title: match.title,
      type: match.type,
      size_key: match.size_key,
      url: match.url
    });

    const color = currentColor || this.state.current.colors?.[0];
    const targetUrl = new URL(match.url, window.location.origin);

    console.log('[giftset-ui] 🧭 navigating to:', targetUrl.toString());
    
    this.fetchAndMorph(targetUrl.toString(), { 
      pushUrl: targetUrl.toString(),
      updateHistory: true 
    });

    this.state.current.type = match.type;
    this.state.current.size_key = match.size_key;
    this.state.current.url = match.url;
    this.state.current.id = match.id;
    if (currentColor) {
      this.state.current.selected_color = currentColor;
    }
  }

  async fetchAndMorph(requestUrl, opts = {}) {
    const signal = this.controller.signal;
    
    const scrollY = window.scrollY || window.pageYOffset;
    const scrollX = window.scrollX || window.pageXOffset;
    
    console.log('[giftset-ui] 💾 BEFORE fetch - scroll position:', { scrollX, scrollY });
    console.log('[giftset-ui] 🌐 fetchAndMorph called:', { requestUrl, opts });
    
    const currentUrl = new URL(window.location.href);
    const targetUrl = new URL(requestUrl, window.location.origin);
    
    if (currentUrl.pathname === targetUrl.pathname && currentUrl.search === targetUrl.search) {
      console.log('[giftset-ui] ⏸️ already on target URL, skipping navigation');
      return;
    }
    
    let html;
    
    try {
      console.log('[giftset-ui] 📡 fetching:', requestUrl);
      const res = await fetch(requestUrl, { 
        signal, 
        headers: { 
          'X-Requested-With': 'XMLHttpRequest',
          'Accept': 'text/html'
        } 
      });
      
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}: ${res.statusText}`);
      }
      
      const text = await res.text();
      console.log('[giftset-ui] ✓ fetch complete, received', text.length, 'characters');
      html = new DOMParser().parseFromString(text, 'text/html');
      console.log('[giftset-ui] ✓ parsed HTML successfully');
    } catch (e) {
      console.error('[giftset-ui] ❌ fetch FAILED:', e);
      console.warn('[giftset-ui] falling back to hard nav');
      if (opts.pushUrl !== false) {
        window.location.href = requestUrl;
      }
      return;
    }

    const currentMain = document.querySelector('main, #MainContent');
    const newMain = html.querySelector('main, #MainContent');
    if (!currentMain || !newMain) {
      console.warn('[giftset-ui] ⚠️ main container not found; hard nav');
      if (opts.pushUrl !== false) {
        window.location.href = opts.pushUrl || requestUrl;
      }
      return;
    }

    console.log('[giftset-ui] 🔄 replacing main element...');
    currentMain.replaceWith(newMain);
    console.log('[giftset-ui] ✓ main element replaced');
    
    const newTitle = html.querySelector('title')?.textContent;
    if (newTitle && newTitle !== document.title) {
      document.title = newTitle;
      console.log('[giftset-ui] ✓ updated document title');
    }

    requestAnimationFrame(() => {
      console.log('[giftset-ui] RAF 1 - preparing scroll restore');
      requestAnimationFrame(() => {
        console.log('[giftset-ui] RAF 2 - restoring scroll NOW');
        
        window.scrollTo(scrollX, scrollY);
        console.log('[giftset-ui] ✓ scroll restored:', { 
          requested: { scrollX, scrollY },
          actual: { x: window.scrollX, y: window.scrollY }
        });
        
        if (opts.pushUrl && opts.pushUrl !== false && opts.pushUrl !== window.location.href) {
          console.log('[giftset-ui] 🔗 updating URL to:', opts.pushUrl);
          
          if (opts.updateHistory) {
            window.history.pushState({ giftsetNavigation: true, scrollY, scrollX }, '', opts.pushUrl);
          } else {
            window.history.replaceState({ giftsetNavigation: true, scrollY, scrollX }, '', opts.pushUrl);
          }
        }
        
        const newPicker = document.querySelector('variant-giftset-picker');
        if (newPicker) {
          console.log('[giftset-ui] ✓ found new picker component');
          
          const storedColor = sessionStorage.getItem('giftset-selected-color');
          const availableColors = newPicker.state?.current?.colors || [];
          
          console.log('[giftset-ui] color restoration check:', {
            storedColor,
            availableColors,
            isAvailable: availableColors.includes(storedColor)
          });
          
          const color = availableColors.includes(storedColor) 
            ? storedColor 
            : availableColors[0];
          
          if (color) {
            console.log('[giftset-ui] ✓ will restore color:', color);
            
            setTimeout(() => {
              console.log('[giftset-ui] 🎨 applying color to new picker');
              newPicker.updateGiftsetColorUI(color);
              newPicker.updateVariantSelection(color);
            }, 150);
          }
        } else {
          console.warn('[giftset-ui] ⚠️ could not find new picker component after morph');
        }
      });
    });

    const color = this.state.current.selected_color || this.getStoredColor() || this.state.current.colors?.[0];
    if (color) {
      console.log('[giftset-ui] 🔄 syncing after morph:', color);
      
      setTimeout(() => {
        this.updateGiftsetColorUI(color);
        this.updateVariantSelection(color);
      }, 50);
    }

    this.dispatchEvent(new CustomEvent('giftset:navigation:complete', {
      detail: {
        url: opts.pushUrl || requestUrl,
        productId: this.state?.current?.id
      },
      bubbles: true
    }));

    console.log('[giftset-ui] ✓ morph complete');
  }
}

if (!customElements.get('variant-giftset-picker')) {
  customElements.define('variant-giftset-picker', VariantGiftsetPicker);
  console.log('[giftset-ui] ✓ Custom element defined');
}

// Handle browser back/forward
window.addEventListener('popstate', (event) => {
  if (event.state && event.state.giftsetNavigation) {
    console.log('[giftset-ui] 🔙 popstate detected, reloading page');
    window.location.reload();
  }
});